import { yupResolver } from "@hookform/resolvers/yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { IconButton, InputAdornment } from "@mui/material";
import { Button as MUIButton } from "@mui/material";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { getAuthActionState } from "app/reducers/authSlice";
import { signInWithCredentials } from "app/thunks/authAPI";
import classNames from "classnames";
import { NAVIGATOR } from "features/navigation/index";
import { SignInData } from "interfaces/requests/Auth";
import { ErrorResponse } from "interfaces/responses/Auth";
import { UserRole } from "interfaces/User";
import { FirebaseAuthErrorCode } from "libraries/firebase/errorMessages";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { PasswordIcon, Spinner, UserIcon } from "ui";
import { BUTTON_STYLES, BUTTON_TYPES } from "ui/Button";
import { TextField } from "ui/TextField";
import { useCustomToast } from "ui/Toast/hook";

import { validationSignInSchema } from "./validations";

export const SignInScreen = () => {
  const { isLoading, user } = useAppSelector(getAuthActionState);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const { showErrorToast } = useCustomToast();

  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    setError,
  } = useForm<SignInData>({
    mode: "onTouched",
    defaultValues: {
      email: undefined,
      password: undefined,
    },
    resolver: yupResolver(validationSignInSchema),
  });

  const onSubmit = async (data: SignInData) => {
    try {
      const response = (await dispatch(
        signInWithCredentials(data),
      ).unwrap()) as boolean;

      if (response) {
        return user?.role === UserRole.admin
          ? history.push(NAVIGATOR.home)
          : history.push(NAVIGATOR.users);
      }

      history.push(NAVIGATOR.updatePassword);
    } catch (error) {
      setErrorMessage(error as ErrorResponse);
    }
  };

  const setErrorMessage = (error: ErrorResponse) => {
    const emailErrors: FirebaseAuthErrorCode[] = [
      "auth/user-not-found",
      "auth/user-disabled",
    ];
    const passwordErrors: FirebaseAuthErrorCode[] = ["auth/wrong-password"];

    if (emailErrors.includes(error.code)) {
      setError("email", {
        type: "manual",
        message: error.message || "Error en el correo electrónico.",
      });
    } else if (passwordErrors.includes(error.code)) {
      setError("password", {
        type: "manual",
        message: error.message || "Error en la contraseña.",
      });
    } else {
      showErrorToast({
        title: "Error",
        message:
          error.message === "Unexpected error."
            ? "Ha ocurrido un error. Por favor ingrese las credenciales nuevamente."
            : error.message,
      });
    }
  };

  return (
    <div className="flex flex-col max-w-xl px-8 justify-center self-center">
      <div className="mb-10 flex flex-col items-center gap-2">
        <p className={"font-medium text-[32px] sm:text-5xl font-orbitron"}>
          ¡Te damos la
        </p>
        <p className={"font-medium text-[32px] sm:text-5xl font-orbitron"}>
          bienvenida!
        </p>
      </div>
      <p className="md:flex items-center text-center font-inter text-sm">
        Ingresa el usuario y la contraseña provista por Radium Rocket.
      </p>
      <form className="flex flex-col mt-10 gap-2">
        <TextField
          type="text"
          placeholder="Nombre de usuario"
          {...register("email")}
          name="email"
          errorMessage={errors.email?.message}
          label="Nombre de Usuario"
          startAdornment={<UserIcon />}
        />
        <div className="mb-10 flex flex-col">
          <TextField
            type={showPassword ? "text" : "password"}
            placeholder="Contraseña"
            {...register("password")}
            errorMessage={errors.password?.message}
            label="Contraseña"
            name="password"
            startAdornment={<PasswordIcon />}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: "#A4A4A4" }}
                >
                  {showPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            }
          />
          <Link
            to="/reset-password"
            className="font-medium font-inter text-auth-gray hover:text-opacity-80 text-xs self-end mt-2"
          >
            Recuperar contraseña
          </Link>
        </div>
        <div className="flex justify-end">
          <MUIButton
            variant="contained"
            disabled={!isValid || isLoading}
            onClick={handleSubmit(onSubmit)}
            className={classNames(BUTTON_STYLES[BUTTON_TYPES.SPACE_AUTH])}
          >
            {isLoading ? <Spinner color="white" /> : <p>Ingresar</p>}
          </MUIButton>
        </div>
      </form>
    </div>
  );
};
